// Spawn primitive. Single source of truth for materialising a fresh
// card instance from a `sorcery-cards.json` catalog row, ready to hand
// to `GameBoard.addCardToTable`.
//
// Replaces the recipe that used to live inline in:
//   - `_spawnTokenAtPoint`              (Necromancer / Midland Army)
//   - `_spawnRubbleAtCell`              (Geomancer earth bonus)
//   - `_resolveGeomancerReplaceRubble`  (Geomancer atlas-replace)
//   - `turnToRubble`                    (Realm-Eater / forced destroy)
//
// Pure: no Three.js, no React, no broadcast, no `addCardToTable`. The
// caller (GameBoard) owns scene insertion + post-spawn pipeline
// (site-play effects, realm-entry triggers).

// Catalog cards that are real, full-size minions/artifacts even though
// they're materialised by card effects rather than hard-cast from hand.
// They render at full card scale and go to the cemetery on death. NOT
// shrunk to 0.5× and banished like ephemeral tokens (Skeleton, Foot
// Soldier, etc.). `kind: 'token'` spawns of these IDs must leave
// `isToken` unset. Shared with the token drawer / browser
// (`buildCatalogTokenEntries`) so both spawn paths agree on size +
// death routing.
//
// Rubble is a Site (spawned via `kind: 'site'`) so it never reaches the
// token branch, but it's kept here as the single canonical list the
// drawer also consumes.
export const FULL_SIZE_SPAWN_IDS = new Set([
  'sorcery-rubble',
  'sorcery-bruin',
  'sorcery-tawny',
  'sorcery-horrible_hybrids',
  'sorcery-eltham_townsfolk',
  'sorcery-serava_townsfolk',
  'sorcery-penitent_knight',
  'sorcery-hellhounds',
  'sorcery-ghoul',
  'sorcery-consecrated_ground',
  'sorcery-desecrated_ground',
  'sorcery-shoggoth',
]);

/**
 * Build a card instance from a Sorcery catalog row.
 *
 * @param {object} cardRow   A row from `sorcery-cards.json`. Must carry
 *                           `unique_id`, `name`, `printings[]`, `type`.
 * @param {object} opts
 * @param {'token'|'site'} opts.kind   Drives type/isSite/isToken flags
 *                                     and the id prefix.
 * @param {boolean} opts.rotated       Controller orientation (p2 = true).
 * @param {number}  opts.x             World-space x to seed the instance.
 * @param {number}  opts.z             World-space z to seed the instance.
 * @returns {object|null}  Instance shape consumed by `addCardToTable`,
 *                         or null if the row is missing a printing.
 */
export function buildSpawnInstance(cardRow, { kind, rotated, x, z }) {
  if (!cardRow) return null;
  const printing = cardRow.printings?.find((p) => p.foiling === 'S') || cardRow.printings?.[0];
  if (!printing) return null;
  const isSite = kind === 'site';
  const isToken = kind === 'token';
  const idPrefix = isSite ? 'site' : 'token';
  const instance = {
    id: `${idPrefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    cardId: cardRow.unique_id,
    name: cardRow.name,
    imageUrl: printing.image_url,
    printingId: printing.unique_id,
    foiling: 'S',
    type: isSite ? 'Site' : cardRow.type,
    isSite,
    subTypes: Array.isArray(cardRow.traits) ? [...cardRow.traits] : [],
    x,
    y: 0,
    z,
    rotated: !!rotated,
  };
  if (isToken && !FULL_SIZE_SPAWN_IDS.has(cardRow.unique_id)) instance.isToken = true;
  return instance;
}
